/**
 * use-offline-state.ts
 *
 * React hook：封裝離線狀態管理邏輯，供 OfflineBanner 等組件使用。
 *
 * 功能：
 * - 監聽 navigator.onLine 狀態變化（online / offline 事件）
 * - 讀取 IndexedDB outbox 中的 pending 條目計數
 * - 啟動自動同步（startAutoSync），並在同步完成後更新計數
 * - 提供 triggerSync() 供用戶手動觸發一次同步
 *
 * 注意：startAutoSync 內部防重複啟動，多個組件掛載此 hook 是安全的。
 */

import { useEffect, useState } from 'react';
import { countFailedOutbox, countPendingOutbox } from '@/pwa/db';
import { syncOnce, startAutoSync } from '@/pwa/sync';

/**
 * 離線狀態 hook 的返回值。
 */
export interface OfflineState {
  /** 當前是否在線 */
  online: boolean;
  /** outbox 中 status='pending' 的條目數量 */
  pending: number;
  /** outbox 中 status='failed' 的條目數量（重試耗盡或 409 衝突，需人工處理） */
  failed: number;
  /** 是否正在執行同步 */
  syncing: boolean;
  /** 上次成功同步的時間，null 表示本次會話尚未同步 */
  lastSyncAt: Date | null;
  /** 手動觸發一次同步並等待完成 */
  triggerSync: () => Promise<void>;
  /** 重新讀取 pending/failed 計數（外部對 outbox 做了重試/放棄操作後刷新用） */
  refreshCounts: () => Promise<void>;
}

/**
 * 離線狀態管理 hook。
 *
 * 使用範例：
 * ```tsx
 * const { online, pending, syncing, triggerSync } = useOfflineState();
 * ```
 */
export function useOfflineState(): OfflineState {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);

  /** 同時刷新 pending 與 failed 計數 */
  const refreshCounts = async () => {
    const [p, f] = await Promise.all([countPendingOutbox(), countFailedOutbox()]);
    setPending(p);
    setFailed(f);
  };

  useEffect(() => {
    /** 網絡恢復事件處理器 */
    const goOnline = () => setOnline(true);
    /** 網絡斷開事件處理器 */
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // 掛載時立即讀取一次 pending / failed 計數
    void refreshCounts();

    // 啟動自動同步（60s 間隔 + online 事件觸發），同步完成後更新計數
    startAutoSync(({ success }) => {
      void refreshCounts();
      if (success > 0) setLastSyncAt(new Date());
    });

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      // 注意：stopAutoSync() 刻意不在此處調用，
      // 因為 autoSync 是全局單例，其他組件可能仍依賴它運行。
    };
  }, []);

  /**
   * 手動觸發一次完整同步。
   * 執行期間 syncing=true，完成後刷新 pending 計數並更新 lastSyncAt。
   */
  const triggerSync = async () => {
    setSyncing(true);
    try {
      await syncOnce();
      await refreshCounts();
      setLastSyncAt(new Date());
    } finally {
      setSyncing(false);
    }
  };

  return { online, pending, failed, syncing, lastSyncAt, triggerSync, refreshCounts };
}
